import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle, Video, Building2, Calendar, Clock, CheckCircle2, ArrowRight, ArrowLeft, Loader2, Download, MapPin } from "lucide-react";
import { Reveal } from "@/components/ui-kit";
import { useLeadForm } from "@/hooks/useLeadForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MODE_ICON = { phone: Phone, whatsapp: MessageCircle, meet: Video, in_person: Building2 };
const FIELD = "w-full rounded-xl border border-navy/10 bg-white px-4 py-3 text-[15px] text-navy placeholder:text-ink/40 outline-none transition-colors focus:border-royal focus:ring-2 focus:ring-royal/20";

const Err = ({ msg }) => (msg ? <p className="mt-1 text-xs text-red-500">{msg}</p> : null);

const Steps = ({ step }) => (
  <ol className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide" data-testid="booking-steps">
    {["Mode & topic", "Date & time", "Your details"].map((l, i) => (
      <li key={l} className="flex items-center gap-2">
        <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] ${i < step ? "bg-gold text-navy" : i === step ? "bg-navy text-white" : "bg-navy/10 text-navy/50"}`}>{i < step ? "✓" : i + 1}</span>
        <span className={`hidden sm:inline ${i === step ? "text-navy" : "text-navy/40"}`}>{l}</span>
        {i < 2 && <span className="mx-1 h-px w-6 bg-navy/10" />}
      </li>
    ))}
  </ol>
);

const ModeStep = ({ config, mode, setMode, topic, setTopic, onNext }) => (
  <div>
    <h3 className="font-display text-xl font-bold text-navy">How would you like to meet?</h3>
    <div className="mt-4 grid grid-cols-2 gap-3">
      {config.modes.map((m) => {
        const I = MODE_ICON[m.key] || Phone;
        const on = mode === m.key;
        return (
          <button key={m.key} type="button" data-testid={`booking-mode-${m.key}`} onClick={() => setMode(m.key)}
            className={`flex flex-col items-start gap-3 rounded-2xl border p-4 text-left transition-colors ${on ? "border-royal bg-royal/[0.06]" : "border-navy/10 bg-white hover:border-navy/30"}`}>
            <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${on ? "bg-royal text-white" : "bg-navy/5 text-royal"}`}><I className="h-5 w-5" /></span>
            <span className="text-sm font-semibold text-navy">{m.label}</span>
          </button>
        );
      })}
    </div>
    {mode === "in_person" && config.office_address && (
      <p data-testid="booking-office-address" className="mt-3 flex items-start gap-2 rounded-xl bg-lightbg px-4 py-3 text-sm text-ink/70"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-royal" /> Office: {config.office_address}</p>
    )}
    <h3 className="mt-8 font-display text-xl font-bold text-navy">What would you like to discuss?</h3>
    <div className="mt-4 flex flex-wrap gap-2">
      {config.topics.map((t) => (
        <button key={t} type="button" data-testid={`booking-topic-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => setTopic(t)}
          className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${topic === t ? "border-navy bg-navy text-white" : "border-navy/10 bg-white text-navy/75 hover:border-navy/40"}`}>{t}</button>
      ))}
    </div>
    <button type="button" data-testid="booking-next-1" disabled={!mode || !topic} onClick={onNext}
      className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-navy shadow-gold transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0">
      Choose date & time <ArrowRight className="h-4 w-4" />
    </button>
  </div>
);

const DateStep = ({ config, date, setDate, slot, setSlot, onBack, onNext }) => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!date) return;
    setLoading(true);
    setSlot(null);
    axios.get(`${API}/booking/slots`, { params: { date } }).then(({ data }) => setSlots(data.slots)).catch(() => setSlots([])).finally(() => setLoading(false));
  }, [date, setSlot]);
  const avail = slots.filter((s) => s.available);
  return (
    <div>
      <h3 className="font-display text-xl font-bold text-navy">Pick a date</h3>
      <p className="mt-1 text-sm text-ink/55">Available {config.hours} IST · {config.slot_minutes}-minute slots</p>
      <div className="mt-4 flex gap-2 overflow-x-auto pb-2 [scrollbar-width:thin]" data-testid="booking-dates">
        {config.days.map((d) => {
          const on = date === d.date;
          return (
            <button key={d.date} type="button" data-testid={`booking-date-${d.date}`} onClick={() => setDate(d.date)}
              className={`flex w-16 shrink-0 flex-col items-center rounded-2xl border py-3 transition-colors ${on ? "border-navy bg-navy text-white" : "border-navy/10 bg-white text-navy hover:border-navy/40"}`}>
              <span className={`text-[11px] font-semibold uppercase ${on ? "text-gold" : "text-ink/45"}`}>{d.weekday}</span>
              <span className="font-display text-xl font-bold">{d.day}</span>
              <span className={`text-[11px] ${on ? "text-white/70" : "text-ink/45"}`}>{d.month}</span>
            </button>
          );
        })}
      </div>
      {date && (
        <>
          <h3 className="mt-8 font-display text-xl font-bold text-navy">Pick a time</h3>
          {loading ? (
            <div className="mt-4 flex items-center gap-2 text-sm text-ink/50"><Loader2 className="h-4 w-4 animate-spin" /> Checking availability...</div>
          ) : avail.length === 0 ? (
            <p data-testid="booking-no-slots" className="mt-4 rounded-xl bg-lightbg px-4 py-3 text-sm text-ink/60">No slots left on this day — please pick another date.</p>
          ) : (
            <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="booking-slots">
              {slots.map((s) => (
                <button key={s.start} type="button" disabled={!s.available} data-testid={`booking-slot-${s.start}`} onClick={() => setSlot(s)}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-30 ${slot?.start === s.start ? "border-royal bg-royal text-white" : "border-navy/10 bg-white text-navy hover:border-navy/40"}`}>
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </>
      )}
      <div className="mt-8 flex flex-wrap gap-3">
        <button type="button" data-testid="booking-back-2" onClick={onBack} className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-6 py-3.5 text-sm font-semibold text-navy"><ArrowLeft className="h-4 w-4" /> Back</button>
        <button type="button" data-testid="booking-next-2" disabled={!date || !slot} onClick={onNext}
          className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-navy shadow-gold transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0">
          Your details <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const DetailsStep = ({ summary, onBack, onBooked, payloadBase }) => {
  const [notes, setNotes] = useState("");
  const lf = useLeadForm({ name: "", mobile: "", email: "" }, (f) => ({ ...f, ...payloadBase, notes }), { endpoint: "/booking", onSuccess: onBooked });
  const serverError = lf.status === "error" ? lf.serverError || "Could not complete the booking. Please try again." : "";

  return (
    <form onSubmit={lf.submit} data-testid="booking-form">
      <div className="rounded-2xl bg-navy p-5 text-white" data-testid="booking-summary">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gold">Your appointment</p>
        <p className="mt-2 flex items-center gap-2 text-sm"><Calendar className="h-4 w-4 text-gold" /> {summary.dateLabel}</p>
        <p className="mt-1 flex items-center gap-2 text-sm"><Clock className="h-4 w-4 text-gold" /> {summary.timeLabel} IST · {summary.modeLabel}</p>
        <p className="mt-1 text-sm text-white/70">{summary.topic}</p>
      </div>
      <div className="mt-6 space-y-3">
        <div><input data-testid="booking-name" className={FIELD} placeholder="Your name" value={lf.form.name} onChange={(e) => lf.set("name", e.target.value)} /><Err msg={lf.errors.name} /></div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><input data-testid="booking-mobile" className={FIELD} placeholder="Mobile number" inputMode="tel" value={lf.form.mobile} onChange={(e) => lf.set("mobile", e.target.value)} /><Err msg={lf.errors.mobile} /></div>
          <div><input data-testid="booking-email" className={FIELD} placeholder="Email" inputMode="email" value={lf.form.email} onChange={(e) => lf.set("email", e.target.value)} /><Err msg={lf.errors.email} /></div>
        </div>
        <textarea data-testid="booking-notes" className={`${FIELD} min-h-[88px]`} placeholder="Anything we should know beforehand? (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        <label className="flex cursor-pointer items-start gap-3 text-xs text-ink/60">
          <input type="checkbox" data-testid="booking-consent" checked={lf.consent} onChange={(e) => lf.setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-royal" />
          <span>I agree to be contacted by Auxieron about this appointment and accept the <a href="/privacy" className="font-semibold text-royal underline">Privacy Policy</a>.</span>
        </label>
        <Err msg={lf.errors.consent} />
        {serverError && <p data-testid="booking-error" className="text-sm text-red-500">{serverError}</p>}
      </div>
      <div className="mt-8 flex flex-wrap gap-3">
        <button type="button" data-testid="booking-back-3" onClick={onBack} className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-6 py-3.5 text-sm font-semibold text-navy"><ArrowLeft className="h-4 w-4" /> Back</button>
        <button type="submit" data-testid="booking-submit" disabled={lf.status === "loading"}
          className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-navy shadow-gold transition-transform hover:-translate-y-0.5 disabled:opacity-60">
          {lf.status === "loading" ? <><Loader2 className="h-4 w-4 animate-spin" /> Booking...</> : <>Confirm booking <CheckCircle2 className="h-4 w-4" /></>}
        </button>
      </div>
    </form>
  );
};

export const BookingConfirmed = ({ appt }) => (
  <div data-testid="booking-confirmed" className="text-center">
    <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 text-gold"><CheckCircle2 className="h-9 w-9" /></span>
    <h3 className="mt-5 font-display text-2xl font-extrabold text-navy sm:text-3xl">You're booked, {appt.name.split(" ")[0]}.</h3>
    <p className="mt-2 text-sm text-ink/60">Reference <strong className="text-navy" data-testid="booking-ref">{appt.ref}</strong>. A confirmation has been emailed to you.</p>
    <div className="mt-6 rounded-2xl border border-navy/[0.08] bg-lightbg/60 p-5 text-left">
      <p className="flex items-center gap-2 text-sm text-navy"><Calendar className="h-4 w-4 text-royal" /> {appt.date_label}</p>
      <p className="mt-1.5 flex items-center gap-2 text-sm text-navy"><Clock className="h-4 w-4 text-royal" /> {appt.time_label} · {appt.mode_label}</p>
      <p className="mt-1.5 text-sm text-ink/60">{appt.topic}</p>
      {appt.office_address && <p className="mt-1.5 flex items-start gap-2 text-sm text-ink/70"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-royal" /> {appt.office_address}</p>}
    </div>
    <div className="mt-6 grid gap-2 sm:grid-cols-3">
      <a data-testid="booking-gcal" href={appt.links.google_calendar} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-navy px-4 py-3 text-sm font-semibold text-white hover:bg-[#0d2c56]"><Calendar className="h-4 w-4" /> Google Calendar</a>
      <a data-testid="booking-ics" href={`${API}/booking/${appt.id}/ics`} className="inline-flex items-center justify-center gap-2 rounded-full border border-navy/15 px-4 py-3 text-sm font-semibold text-navy hover:border-navy/40"><Download className="h-4 w-4" /> .ics file</a>
      <a data-testid="booking-whatsapp" href={appt.links.whatsapp} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366]/15 px-4 py-3 text-sm font-semibold text-[#128C7E] hover:bg-[#25D366]/25"><MessageCircle className="h-4 w-4" /> WhatsApp us</a>
    </div>
  </div>
);

export const BookingWidget = ({ defaultTopic }) => {
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState(null);
  const [topic, setTopic] = useState(defaultTopic || null);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/booking/config`).then(({ data }) => { setConfig(data); if (data.modes.length === 1) setMode(data.modes[0].key); });
  }, []);

  if (!config) return <div className="flex justify-center py-16 text-navy/40"><Loader2 className="h-7 w-7 animate-spin" /></div>;
  if (done) return <BookingConfirmed appt={done} />;

  const day = config.days.find((d) => d.date === date);
  const summary = { dateLabel: day?.label, timeLabel: slot ? `${slot.label} – ${slot.end_label}` : "", modeLabel: config.modes.find((m) => m.key === mode)?.label, topic };

  return (
    <div data-testid="booking-widget" className="min-w-0">
      <Steps step={step} />
      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }} className="mt-8">
          {step === 0 && <ModeStep config={config} mode={mode} setMode={setMode} topic={topic} setTopic={setTopic} onNext={() => setStep(1)} />}
          {step === 1 && <DateStep config={config} date={date} setDate={setDate} slot={slot} setSlot={setSlot} onBack={() => setStep(0)} onNext={() => setStep(2)} />}
          {step === 2 && <DetailsStep summary={summary} payloadBase={{ mode, topic, date, start: slot.start }} onBack={() => setStep(1)} onBooked={setDone} />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export const BookingReveal = () => <Reveal><BookingWidget /></Reveal>;
